import {
  fetchContinuationVoice,
  getContinuationVoiceMeta,
  type OpsContinuationVoice,
} from './opsApi';
import { safeGetItem, safeSetItem } from './storage';

export interface CachedContinuationVoice {
  cacheKey: string;
  text: string;
  mimeType: string;
  audioBase64: string;
  voice: string;
  model: string;
  staffId: string;
  createdAt: string;
}

interface ActiveContinuationAudio {
  audio: HTMLAudioElement;
  url: string;
  released: boolean;
}

let activeAudio: ActiveContinuationAudio | null = null;

function releaseAudio(active: ActiveContinuationAudio): void {
  if (active.released) return;
  active.released = true;
  active.audio.onended = null;
  active.audio.onerror = null;
  if (activeAudio === active) activeAudio = null;
  try {
    // Clearing src interrupts playback without calling pause/load, which jsdom
    // does not implement. The object URL still must be released explicitly.
    active.audio.src = '';
  } catch {
    /* ignore cleanup failures */
  }
  URL.revokeObjectURL(active.url);
}

function storageKey(cacheKey: string) {
  return cacheKey;
}

export function readCachedContinuationVoice(cacheKey: string): CachedContinuationVoice | null {
  const raw = safeGetItem(storageKey(cacheKey));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CachedContinuationVoice;
    if (!parsed?.audioBase64 || !parsed?.mimeType) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeCachedContinuationVoice(payload: OpsContinuationVoice): void {
  const value: CachedContinuationVoice = {
    cacheKey: payload.cacheKey,
    text: payload.text,
    mimeType: payload.mimeType,
    audioBase64: payload.audioBase64,
    voice: payload.voice,
    model: payload.model,
    staffId: payload.staffId,
    createdAt: new Date().toISOString(),
  };
  try {
    safeSetItem(storageKey(payload.cacheKey), JSON.stringify(value));
  } catch {
    /* quota — play without durable cache */
  }
}

/**
 * Prefer localStorage cache for current speech meta.
 * On miss, call backend Gemini TTS once, cache, then play.
 * Returns true if audio started playing.
 */
export async function playContinuationVoice(): Promise<boolean> {
  stopContinuationVoice();

  let payload: OpsContinuationVoice | CachedContinuationVoice | null = null;

  try {
    const meta = await getContinuationVoiceMeta();
    payload = readCachedContinuationVoice(meta.cacheKey);
    if (!payload) {
      const remote = await fetchContinuationVoice();
      writeCachedContinuationVoice(remote);
      payload = remote;
    }
  } catch {
    return false;
  }

  return playBase64Audio(payload.mimeType, payload.audioBase64);
}

async function playBase64Audio(mimeType: string, audioBase64: string): Promise<boolean> {
  let active: ActiveContinuationAudio | null = null;
  try {
    const binary = atob(audioBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: mimeType || 'audio/wav' });
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    active = { audio, url, released: false };
    activeAudio = active;
    audio.onended = () => {
      releaseAudio(active!);
    };
    audio.onerror = () => {
      releaseAudio(active!);
    };
    await audio.play();
    return true;
  } catch {
    if (active) releaseAudio(active);
    return false;
  }
}

export function stopContinuationVoice(): void {
  if (!activeAudio) return;
  releaseAudio(activeAudio);
}
